/**
 * BreedAutocomplete Component
 * Autocomplete dropdown for breed selection using get_species_breeds RPC
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, ChevronDown, X, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/services";

interface Breed {
  id: string;
  species_key: string;
  name: string;
  popularity: number;
  is_mixed: boolean;
  is_other: boolean;
  is_active: boolean;
  sort_order: number;
}

interface BreedAutocompleteProps {
  speciesKey: "dog" | "cat";
  value: string; // breed name (display value)
  breedId?: string | null; // breed UUID
  onSelect: (breed: Breed | null) => void;
  placeholder?: string;
  className?: string;
}

export function BreedAutocomplete({
  speciesKey,
  value,
  breedId,
  onSelect,
  placeholder = "Выберите породу",
  className,
}: BreedAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch breeds from RPC
  const fetchBreeds = useCallback(async (searchQuery: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_species_breeds", {
        p_species_key: speciesKey,
        p_query: searchQuery || null,
        p_limit: 50,
        p_offset: 0,
        p_include_inactive: false,
        p_include_other: true,
        p_include_mixed: true,
      });

      if (error) {
        logger.error("Error fetching breeds", {
          context: { feature: "breeds", function_name: "fetchBreeds" },
          error,
          error_code: "breeds_fetch_failed",
          error_type: "unexpected",
        });
        setBreeds([]);
      } else {
        setBreeds((data as Breed[]) || []);
      }
    } catch (err) {
      logger.error("Error fetching breeds", {
        context: { feature: "breeds", function_name: "fetchBreeds" },
        error: err as Error,
        error_code: "breeds_fetch_failed",
        error_type: "unexpected",
      });
      setBreeds([]);
    } finally {
      setIsLoading(false);
    }
  }, [speciesKey]);

  // Initial load + search debounce
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        fetchBreeds(query);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, query, fetchBreeds]);

  // Reset when species changes
  useEffect(() => {
    setQuery("");
    setBreeds([]);
  }, [speciesKey]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSelect = (breed: Breed) => {
    onSelect(breed);
    setIsOpen(false);
    setQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
    setQuery("");
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger button */}
      <button
        type="button"
        data-testid="ads-002-breed-trigger"
        onClick={handleOpen}
        className="w-full h-input rounded-input bg-bg-secondary border-0 text-left px-4 flex items-center justify-between"
      >
        <span className={value ? "text-text-primary" : "text-text-tertiary"}>
          {value || placeholder}
        </span>
        <div className="flex items-center gap-1">
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-bg-tertiary rounded-full"
              aria-label="Очистить"
            >
              <X className="w-4 h-4 text-text-tertiary" />
            </button>
          )}
          <ChevronDown
            className={cn(
              "w-5 h-5 text-text-tertiary transition-transform",
              isOpen && "rotate-180"
            )}
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-bg-primary rounded-input shadow-lg border border-border-tertiary z-20 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-border-tertiary">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
              <input
                ref={inputRef}
                type="text"
                data-testid="ads-002-breed-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск породы..."
                className="w-full h-10 pl-9 pr-4 rounded-input bg-bg-secondary border-0 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-text-accent-pink"
              />
            </div>
          </div>

          {/* Breeds list */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-3 text-center text-sm text-text-tertiary">
                Загрузка...
              </div>
            ) : breeds.length === 0 ? (
              <div className="px-4 py-3 text-center text-sm text-text-tertiary">
                {query ? "Ничего не найдено" : "Нет доступных пород"}
              </div>
            ) : (
              breeds.map((breed) => (
                <button
                  key={breed.id}
                  type="button"
                  data-testid={`ads-002-breed-option-${breed.id}`}
                  onClick={() => handleSelect(breed)}
                  className={cn(
                    "w-full px-4 py-3 text-left text-sm flex items-center justify-between hover:bg-bg-secondary transition-colors",
                    breedId === breed.id && "bg-bg-accent-pink"
                  )}
                >
                  <span className="text-text-primary">
                    {breed.name}
                    {breed.is_mixed && (
                      <span className="ml-2 text-xs text-text-tertiary">(метис)</span>
                    )}
                  </span>
                  {breedId === breed.id && (
                    <Check className="w-4 h-4 text-text-accent-pink" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BreedAutocomplete;
